import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import useVendorStore from "../../HomePage/useVendorStore";
import useCommonStore from "../../Common/useCommonStore";
import { DownloadInvoice, getPdfBytes } from "../../../../operations/operations";
import { PO_ORDER_TAG_DIRECT_DELIVERY, PO_ORDER_TAG_MANDI_DD, STATUS_BEGIN } from "utils/constants/others";
import { DD_PO_PAGE_CONFIG, DD_PO_PAGES, getDDPoPageConfig } from "./config";

const calculateTonnage = (items = []) =>
  items.reduce((total, item) => total + Number(item.quantity_ordered || 0) * Number(item.weight_per_packet || 0), 0);

const formatWeight = (tonnage) =>
  tonnage > 999 ? `${(tonnage / 1000).toFixed(2)} tonne` : `${tonnage.toFixed(2)} kg`;

export default function useAllDDPoViewLogic() {
  const navigate = useNavigate();
  const outletId = window.localStorage.getItem("outlet_id");
  const {
    getPurchaseOrders,
    purchaseOrders,
    poCount,
    getPurchaseOrderStatus,
  } = useVendorStore();
  const uploadDocs = useCommonStore((state) => state.uploadDocs);

  const [activePage, setActivePage] = useState(DD_PO_PAGES.PENDING_INVOICE);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const fetchOrders = useCallback(
    (requestedPageNumber, isFirstPage) => {
      const config = getDDPoPageConfig(activePage);
      getPurchaseOrders(
        {
          ...config.getParams(outletId),
          page_no: requestedPageNumber,
        },
        isFirstPage
      );
    },
    [activePage, getPurchaseOrders, outletId]
  );

  useEffect(() => {
    setPageNumber(1);
    fetchOrders(1, true);
  }, [activePage, fetchOrders]);

  useEffect(() => {
    setHasNextPage(purchaseOrders.length < Number(poCount || 0));
  }, [purchaseOrders, poCount]);

  const showLoader = getPurchaseOrderStatus === STATUS_BEGIN && pageNumber === 1;

  const handlePageChange = (page) => {
    if (page === activePage) {
      return;
    }
    setActivePage(page);
  };

  const loadNextPage = () => {
    if (getPurchaseOrderStatus === STATUS_BEGIN || !hasNextPage) {
      return;
    }
    const nextPage = pageNumber + 1;
    fetchOrders(nextPage, false);
    setPageNumber(nextPage);
  };

  const goToPage = (requestedPageNumber) => {
    if (getPurchaseOrderStatus === STATUS_BEGIN || requestedPageNumber === pageNumber) {
      return;
    }
    fetchOrders(requestedPageNumber, true);
    setPageNumber(requestedPageNumber);
  };

  const downloadBlobAndSendJsEvent = async (url, poNumber) => {
    const fileBytes = await getPdfBytes(url);
    const formData = new FormData();
    const blob = new Blob([fileBytes], { type: "application/pdf" });
    const fileName = `Invoice-${poNumber}-${uuidv4()}.pdf`;
    formData.append("fileType", "supplier_bill_image");
    formData.append("file", blob);
    formData.append("fileName", fileName);
    uploadDocs(formData, (uploadedUrl) => {
      if (window?.downloadFileMessageHandler) {
        window.downloadFileMessageHandler.postMessage(uploadedUrl);
      }
    });
  };

  const handleDownloadInvoice = (po) => {
    if (!po.order_bill_image) {
      return;
    }
    DownloadInvoice(po.order_bill_image, `Invoice-${po.purchase_order_number}`);
    if (window?.downloadFileMessageHandler) {
      downloadBlobAndSendJsEvent(po.order_bill_image, po.purchase_order_number);
    }
  };

  const handleViewDetails = (po, source) => {
    navigate(`/ddPo/details/${source}/${po.id}`);
  };

  const handleViewPayment = (po) => {
    setSelectedPayment(po.purchase_order_payment || null);
  };

  const ddPoTableRowData = useMemo(() => {
    return purchaseOrders.map((po) => {
      const items = po.purchase_order_items || [];
      const tonnage = calculateTonnage(items);
      const isMandi = po.order_tag === PO_ORDER_TAG_MANDI_DD;
      const buyerOutletName =
        po.order_tag === PO_ORDER_TAG_DIRECT_DELIVERY ? po.buyer_outlet_details?.name || "" : "";

      return {
        id: po.id,
        poNumber: po.purchase_order_number,
        isMandi,
        skuCount: items.length,
        netWeight: formatWeight(tonnage),
        deliverBy: po.expected_delivery_date,
        poAmount: po.po_amount,
        deliveryAt: buyerOutletName,
        hasInvoice: Boolean(po.order_bill_image?.trim?.()),
        invoiceAmount: po.total_supplier_bill_amount,
        isPaid: Boolean(po?.purchase_order_payment?.id),
        paymentDate: po?.purchase_order_payment?.payment_date,
        deliveredOn: po.po_consideration_date,
        payment: po.purchase_order_payment,
        _raw: po,
      };
    });
  }, [purchaseOrders]);

  return {
    activePage,
    pageConfig: DD_PO_PAGE_CONFIG,
    pageNumber,
    hasNextPage,
    showLoader,
    purchaseOrders,
    ddPoTableRowData,
    handlePageChange,
    loadNextPage,
    goToPage,
    handleDownloadInvoice,
    handleViewDetails,
    handleViewPayment,
    selectedPayment,
    setSelectedPayment,
  };
}
